import { useEffect } from 'react';

export interface MetaTagsConfig {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  author?: string;
  robotsContent?: string;
  themeColor?: string;
  structuredData?: object;
}

const DEFAULT_CONFIG: Partial<MetaTagsConfig> = {
  ogImage: 'https://cdn.thrivebot.in/review-user-avatars/thrivebot.webp',
  twitterImage: 'https://cdn.thrivebot.in/review-user-avatars/thrivebot.webp',
  author: 'Thrivebot Developers',
  robotsContent: 'index, follow',
  themeColor: '#6366f1',
};

export const useMetaTags = (config: MetaTagsConfig) => {
  useEffect(() => {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    
    // Update document title
    document.title = finalConfig.title;
    
    // Helper function to update or create meta tag
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };
    
    // Helper function to update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      
      if (link) {
        link.setAttribute('href', href);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('href', href);
        document.head.appendChild(link);
      }
    };
    
    // Basic meta tags
    updateMetaTag('description', finalConfig.description);
    if (finalConfig.keywords) {
      updateMetaTag('keywords', finalConfig.keywords);
    }
    if (finalConfig.author) {
      updateMetaTag('author', finalConfig.author);
    }
    if (finalConfig.robotsContent) {
      updateMetaTag('robots', finalConfig.robotsContent);
    }
    if (finalConfig.themeColor) {
      updateMetaTag('theme-color', finalConfig.themeColor);
    }
    
    // Open Graph meta tags
    updateMetaTag('og:title', finalConfig.ogTitle || finalConfig.title, true);
    updateMetaTag('og:description', finalConfig.ogDescription || finalConfig.description, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', finalConfig.ogUrl || window.location.href, true);
    if (finalConfig.ogImage) {
      updateMetaTag('og:image', finalConfig.ogImage, true);
      updateMetaTag('og:image:alt', `${finalConfig.title} - Thrivebot`, true);
      updateMetaTag('og:image:width', '1200', true);
      updateMetaTag('og:image:height', '630', true);
    }
    updateMetaTag('og:site_name', 'Thrivebot', true);
    updateMetaTag('og:locale', 'en_US', true);
    
    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:site', '@Thrivebot');
    updateMetaTag('twitter:creator', '@Thrivebot');
    updateMetaTag('twitter:title', finalConfig.twitterTitle || finalConfig.ogTitle || finalConfig.title);
    updateMetaTag('twitter:description', finalConfig.twitterDescription || finalConfig.ogDescription || finalConfig.description);
    if (finalConfig.twitterImage || finalConfig.ogImage) {
      updateMetaTag('twitter:image', finalConfig.twitterImage || finalConfig.ogImage!);
    }
    
    // Discord-specific meta tags for better embeds
    updateMetaTag('discord:embed:color', '#6366f1');
    
    // Canonical URL
    if (finalConfig.canonical) {
      updateLinkTag('canonical', finalConfig.canonical);
    }
    
    // Structured data (JSON-LD)
    if (finalConfig.structuredData) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (scriptTag) {
        scriptTag.textContent = JSON.stringify(finalConfig.structuredData);
      } else {
        scriptTag = document.createElement('script') as HTMLScriptElement;
        scriptTag.type = 'application/ld+json';
        scriptTag.textContent = JSON.stringify(finalConfig.structuredData);
        document.head.appendChild(scriptTag);
      }
    }
  }, [config]);
};
